import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import html2pdf from 'html2pdf.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const PenalitatiANAF = () => {
  const [sumaDePlata, setSumaDePlata] = useState('');
  const [dataScadenta, setDataScadenta] = useState(null);
  const [dataPlata, setDataPlata] = useState(null);
  const [zileIntarziere, setZileIntarziere] = useState('');
  const [penalitati001, setPenalitati001] = useState('0.00');
  const [dobanda002, setDobanda002] = useState('0.00');
  const [penalitatiPlusDobanda, setPenalitatiPlusDobanda] = useState('0.00');
  const [totalDePlata, setTotalDePlata] = useState('0.00');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Calculează automat când se schimbă valorile
  useEffect(() => {
    if (sumaDePlata && dataScadenta && dataPlata) {
      const suma = parseFloat(sumaDePlata);
      const scadenta = new Date(dataScadenta);
      const plata = new Date(dataPlata);
      
      if (suma > 0 && plata > scadenta) {
        // Calculează numărul de zile de întârziere
        const diferentaMs = plata.getTime() - scadenta.getTime();
        const zile = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
        
        // Calculează penalitățile și dobânzile
        const penalitate001 = (suma * 0.01 / 100) * zile; // 0.01% pe zi
        const dobanda002 = (suma * 0.02 / 100) * zile; // 0.02% pe zi
        const penalitatiSiDobanda = penalitate001 + dobanda002;
        const total = suma + penalitatiSiDobanda;
        
        setZileIntarziere(zile.toString());
        setPenalitati001(penalitate001.toFixed(2));
        setDobanda002(dobanda002.toFixed(2));
        setPenalitatiPlusDobanda(penalitatiSiDobanda.toFixed(2));
        setTotalDePlata(total.toFixed(2));
      } else if (plata <= scadenta) {
        // Nu există penalități
        setZileIntarziere('0');
        setPenalitati001('0.00');
        setDobanda002('0.00');
        setPenalitatiPlusDobanda('0.00');
        setTotalDePlata(suma ? suma.toFixed(2) : '0.00');
      }
    } else {
      setZileIntarziere('');
      setPenalitati001('0.00');
      setDobanda002('0.00');
      setPenalitatiPlusDobanda('0.00');
      setTotalDePlata('0.00');
    }
  }, [sumaDePlata, dataScadenta, dataPlata]);

  const resetCalculator = () => {
    setSumaDePlata('');
    setDataScadenta(null);
    setDataPlata(null);
    setZileIntarziere('');
    setPenalitati001('0.00');
    setDobanda002('0.00');
    setPenalitatiPlusDobanda('0.00');
    setTotalDePlata('0.00');
  };

  const copiazaRezultat = () => {
    if (zileIntarziere && parseFloat(zileIntarziere) > 0) {
      const textDeCopiat = `Pentru ${zileIntarziere} zile întârziere, penalitățile ANAF sunt: ${penalitatiPlusDobanda} lei (${penalitati001} lei penalități + ${dobanda002} lei dobândă). Suma totală de plată: ${totalDePlata} lei.`;
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = () => {
    if (zileIntarziere && parseFloat(zileIntarziere) > 0) {
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            Calculator Penalități ANAF
          </h2>
          
          <div style="margin: 20px 0;">
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Sumă de plată:</strong></span>
              <span><strong>${sumaDePlata} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Data scadentă:</strong></span>
              <span><strong>${dataScadenta ? dataScadenta.toLocaleDateString('ro-RO') : ''}</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Data plății:</strong></span>
              <span><strong>${dataPlata ? dataPlata.toLocaleDateString('ro-RO') : ''}</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Zile întârziere:</strong></span>
              <span><strong>${zileIntarziere} zile</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Penalități 0.01%:</strong></span>
              <span style="color: #dc3545;"><strong>${penalitati001} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Dobândă 0.02%:</strong></span>
              <span style="color: #dc3545;"><strong>${dobanda002} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Penalități + Dobândă:</strong></span>
              <span style="color: #dc3545;"><strong>${penalitatiPlusDobanda} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 2px solid #ccc;">
              <span><strong>Total de plată:</strong></span>
              <span style="color: #28a745; font-size: 18px;"><strong>${totalDePlata} lei</strong></span>
            </div>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: 'penalitati-anaf.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <CalculateIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Penalități ANAF
      </Typography>
      
      <Card sx={{ marginTop: 2 }}>
        <CardContent>
          <form style={{ overflowX: 'visible' }}>
            <Grid container spacing={3}>
              {/* Sumă de plată */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Sumă de plată <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={sumaDePlata}
                    onChange={(e) => setSumaDePlata(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0, max: 9999999, step: 'any' }}
                  />
                </Box>
              </Grid>

              {/* Data scadentă */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Data scadentă <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <DatePicker
                    selected={dataScadenta}
                    onChange={(date) => setDataScadenta(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="zi.luna.an"
                    customInput={
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                    }
                  />
                </Box>
              </Grid>

              {/* Data plății */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Data plății <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <DatePicker
                    selected={dataPlata}
                    onChange={(date) => setDataPlata(date)}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="zi.luna.an"
                    customInput={
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                    }
                  />
                </Box>
              </Grid>

              {/* Zile întârziere */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Zile întârziere <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    type="text"
                    value={zileIntarziere}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Box>
              </Grid>

              {/* Penalități 0.01% */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Penalități 0.01% <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Paper 
                    sx={{ 
                      padding: 2, 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #dee2e6',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                      {penalitati001}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>

              {/* Dobândă 0.02% */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Dobândă 0.02% <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Paper 
                    sx={{ 
                      padding: 2, 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #dee2e6',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                      {dobanda002}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>

              {/* Penalități + Dobândă */}
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Penalități 0.01% + Dobândă 0.02% <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Paper 
                    sx={{ 
                      padding: 3, 
                      backgroundColor: '#fff3cd', 
                      border: '2px solid #ffeaa7',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#856404' }}>
                      {penalitatiPlusDobanda}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>

              {/* Total de plată */}
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Total de plată - debit + dob. + pen. <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Paper 
                    sx={{ 
                      padding: 3, 
                      backgroundColor: '#d4edda', 
                      border: '2px solid #c3e6cb',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#155724' }}>
                      {totalDePlata}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>

              {/* Butoane */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={resetCalculator}
                  >
                    Reset
                  </Button>
                  
                  {zileIntarziere && parseFloat(zileIntarziere) > 0 && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={exportaPDF}
                      >
                        Export PDF
                      </Button>
                      
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ContentCopyIcon />}
                        onClick={copiazaRezultat}
                      >
                        Copiază rezultatul
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PenalitatiANAF;